import { useRolodexSignedURLs } from 'features/rolodex'
import { CalibrationPageData } from 'pages/reporting/reports/raut/rautReportData'
import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchImageAsBase64 } from 'utils/imageUtils'

export interface PDFImageData {
  imagePath: string
  imageCaption?: string
  signedUrl?: string
}

export const useCalibrationImage = (
  metadataEntryId: string,
  calibrationData?: CalibrationPageData
): PDFImageData | undefined => {
  const [base64Image, setBase64Image] = useState<PDFImageData | undefined>(
    undefined
  )

  // Extract the calibration image path if it exists
  const calibrationImagePath = calibrationData?.calibrationImage?.imagePath
  const calibrationImageCaption =
    calibrationData?.calibrationImage?.imageCaption

  // Get signed URL for the calibration image
  const { results: signedUrlResults } = useRolodexSignedURLs(
    calibrationImagePath
      ? [
          {
            entry_id: metadataEntryId,
            path: calibrationImagePath,
          },
        ]
      : []
  )

  // Resolve signed URL using stable primitive values to avoid re-triggering
  // the base64 conversion effect on every formData reference change
  const resolvedSignedUrl = useMemo(() => {
    if (!calibrationImagePath) return undefined
    return signedUrlResults.find(
      (result) => result.path === calibrationImagePath
    )?.signed_url
  }, [calibrationImagePath, signedUrlResults])

  // Track the last successfully converted URL to avoid redundant conversions
  const lastConvertedUrlRef = useRef<string | undefined>(undefined)

  // Pre-fetch and convert to base64 for reliable PDF rendering
  useEffect(() => {
    if (!resolvedSignedUrl || !calibrationImagePath) {
      setBase64Image(undefined)
      lastConvertedUrlRef.current = undefined
      return
    }

    // Skip re-conversion if we already converted this exact signed URL
    if (lastConvertedUrlRef.current === resolvedSignedUrl) {
      return
    }

    let cancelled = false

    fetchImageAsBase64(resolvedSignedUrl)
      .then((base64Url) => {
        if (!cancelled) {
          lastConvertedUrlRef.current = resolvedSignedUrl
          setBase64Image({
            imagePath: calibrationImagePath,
            imageCaption: calibrationImageCaption,
            signedUrl: base64Url,
          })
        }
      })
      .catch((error) => {
        console.error(
          '[useCalibrationImage] Failed to convert image to base64:',
          error
        )
        if (!cancelled) {
          setBase64Image(undefined)
        }
      })

    return () => {
      cancelled = true
    }
  }, [resolvedSignedUrl, calibrationImagePath, calibrationImageCaption])

  return base64Image
}
